import { api, ApiError } from "@/lib/api-client";
import type { OverrideBehavior, OverrideConfig } from "@capture/shared/types";
import { describeValidateRuleConfig, type ValidateRuleConfig } from "@capture/rules-engine";

/**
 * R-239 Plan B (B4) — what Ctrl+O does for a failing field, derived from its
 * override policy (the field's `overrideBehavior` + the rule's `overrideConfig`).
 */
export type OverrideAction =
  | { kind: "refuse" }
  | { kind: "quick"; fallbackValue?: string }
  | { kind: "dialog"; requireReason: boolean; fallbackValue?: string; escalationQueue?: string }
  | { kind: "escalate"; requireReason: boolean; escalationQueue?: string };

/**
 * Decide the override path for a failing field:
 * - block / absent           → refuse (hard rule, no bypass)
 * - override + requireReason  → open the dialog to capture a reason
 * - override + !requireReason → quick-confirm (audit-only, persist immediately)
 * - escalate                  → route to escalation (reason via dialog)
 */
export function resolveOverrideAction(
  behavior: OverrideBehavior | undefined,
  config?: OverrideConfig,
): OverrideAction {
  if (behavior === "override") {
    const requireReason = config?.requireReason ?? false;
    if (requireReason) {
      return {
        kind: "dialog",
        requireReason: true,
        fallbackValue: config?.fallbackValue,
        escalationQueue: config?.escalationQueue,
      };
    }
    return { kind: "quick", fallbackValue: config?.fallbackValue };
  }
  if (behavior === "escalate") {
    return {
      kind: "escalate",
      requireReason: config?.requireReason ?? false,
      escalationQueue: config?.escalationQueue,
    };
  }
  // "block" or undefined — the field cannot be overridden.
  return { kind: "refuse" };
}

/** E-043 — context resolved at the Ctrl+O call site (field label, the
 *  failed rule's identity + human message) so the audit trail can say WHAT
 *  was overridden instead of just "field". Purely additive — omitting it
 *  keeps today's payload byte-identical. */
export interface OverrideAuditContext {
  fieldLabel?: string;
  ruleType?: string;
  ruleMessage?: string;
}

/**
 * E-043 follow-up — THE single seam that decides which human message an
 * override audit entry carries: the author's custom message if the rule has
 * one, else a config-derived one-liner ("must be greater than 0") for a
 * default-message rule, else the resolved fallback (the generic per-type
 * default). Lives client-side because only the station holds the resolved
 * `ValidateRuleConfig` (via `pickActiveEntry`) — deriving this server-side would
 * mean re-resolving the active binding there. Pure; `rule.message` is the raw
 * authored message (absent → default-message rule).
 */
export function resolveOverrideRuleMessage(
  rule: ValidateRuleConfig | undefined,
  fallbackMessage: string | undefined,
): string | undefined {
  if (rule && !rule.message) {
    const summary = describeValidateRuleConfig(rule);
    if (summary) return summary;
  }
  return fallbackMessage;
}

/**
 * Persist a field override via `POST /documents/:id/override`. The server
 * audits + is step-gated; a 403 means the operator lacks override permission
 * at the current step (defect #4 enforcement) and must be surfaced distinctly.
 */
export async function persistFieldOverride(
  documentId: string,
  fieldId: string,
  reason: string,
  fallbackValue?: string,
  context?: OverrideAuditContext,
): Promise<{ ok: boolean; forbidden: boolean }> {
  try {
    await api.post(`/documents/${documentId}/override`, {
      fieldId,
      reason,
      ...(fallbackValue !== undefined ? { fallbackValue } : {}),
      ...(context?.fieldLabel ? { fieldLabel: context.fieldLabel } : {}),
      ...(context?.ruleType ? { ruleType: context.ruleType } : {}),
      ...(context?.ruleMessage ? { ruleMessage: context.ruleMessage } : {}),
    });
    return { ok: true, forbidden: false };
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) {
      return { ok: false, forbidden: true };
    }
    return { ok: false, forbidden: false };
  }
}
